<template>
    <!-- TODO: Think about better aria-label value -->
    <component
        :is="computedTag"
        :aria-label="iconLeft"
        :disabled="disabledOrHydrating"
        :class="computedClasses"
        :href="props.href"
        :type="computedType"
        :target="computedTarget"
        class="inline-flex items-center justify-center group relative"
    >
        <div
            class="flex-center"
            :class="contentHolderClasses"
        >
            <slot>
                <BasicIcon
                    v-if="iconLeft"
                    :name="iconLeft"
                    :size="computedIconSize"
                    :class="computedIconClassLeft"
                />
                <div
                    v-if="!iconOnly"
                    :class="textHolderClasses"
                >
                    {{ text }}
                </div>
                <BasicIcon
                    v-if="iconRight && !iconOnly"
                    :name="iconRight"
                    :size="computedIconSize"
                    :class="computedIconClassRight"
                />
            </slot>
        </div>
    </component>
</template>

<script setup lang="ts">
import {
    colors,
    iconSizes,
    sizeCircleClasses,
    sizeClasses,
    sizeRelatedContentHolderClasses,
    sizeRelatedTextHolderClasses,
    sizes,
    tags,
    targetAttributes,
    typeAttributes,
    widthClasses,
    widths,
    type Color,
    type Size,
    type Tag,
    type TargetAttribut,
    type TypeAttribut,
    type Width,
} from '@atoms/BasicButton/config';
import BasicIcon from '@atoms/BasicIcon/BasicIcon.vue';

import { useIsHydrating } from '@composables/useIsHydrating';

const props = defineProps({
    tag: {
        type: String as PropType<Tag>,
        default: 'button',
        validator: (value: Tag) => tags.includes(value),
    },
    text: {
        type: String,
        default: '',
    },
    size: {
        type: String as PropType<Size>,
        default: 'lg',
        validator: (value: Size) => sizes.includes(value),
    },
    color: {
        type: String as PropType<Color>,
        default: 'primary',
        validator: (value: Color) => Object.keys(colors).includes(value),
    },
    type: {
        type: String as PropType<TypeAttribut>,
        default: 'button',
        validator: (value: TypeAttribut) =>
            typeAttributes.includes(value) || value === null,
    },
    superviseHydration: {
        type: Boolean,
        default: false,
    },
    href: {
        type: String,
        default: null,
    },
    target: {
        type: String as PropType<TargetAttribut>,
        default: '_self',
        validator: (value: TargetAttribut) => targetAttributes.includes(value),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    uppercase: {
        type: Boolean,
        default: true,
    },
    width: {
        type: String as PropType<Width>,
        default: 'w-none',
        validator: (value: Width) => widths.includes(value),
    },
    iconLeft: {
        type: String,
        default: null,
    },
    iconRight: {
        type: String,
        default: null,
    },
    iconOnly: {
        type: Boolean,
        default: false,
    },
    isLink: {
        type: Boolean,
        default: false,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    isInsideText: {
        type: Boolean,
        default: false,
    },
});

export type BasicButtonProps = {
    tag: Tag;
    text: string;
    size: Size;
    color: Color;
    type: TypeAttribut;
    isLink: boolean;
    disabled: boolean;
    uppercase: boolean;
    href: string;
    target: TargetAttribut;
    width: Width;
    iconLeft: string;
    iconRight: string;
    iconOnly: boolean;
    isLoading?: boolean;
    isInsideText?: boolean;
};

const { isHydrating } = useIsHydrating();

const disabledOrHydrating = computed(() =>
    props.superviseHydration
        ? props.disabled || isHydrating.value
        : props.disabled
);

const computedTag = computed(() => {
    return props.href ? 'a' : props.tag;
});

const computedType = computed(() => {
    return props.href ? null : props.type;
});

const computedTarget = computed(() => {
    return props.href ? props.target : undefined;
});

const computedColor = computed(() => {
    return colors[props.color];
});

const computedClasses = computed(() => {
    const widthClass = widthClasses[props.width];
    const buttonSizeClasses = sizeClasses[props.size];
    const buttonSizeCircleClasses = sizeCircleClasses[props.size];

    return [
        'fade',
        'rounded-full',
        'tracking-[-0.14px]',
        'focus-outline',
        widthClass,
        buttonSizeClasses,
        {
            'font-bold': !props.isInsideText,
            'font-light': props.isInsideText,
            loading: props.isLoading,
            'opacity-75': disabledOrHydrating.value,
            'cursor-default': disabledOrHydrating.value,
            uppercase: props.uppercase,
            [buttonSizeCircleClasses]: props.iconOnly,
            [computedColor.value.bg]: !props.isLink,
            [computedColor.value.bgHover]:
                !disabledOrHydrating.value && !props.isLink,
            [computedColor.value.text]: !props.isLink,
            [computedColor.value.textLink]: props.isLink,
            [computedColor.value.textHover]:
                !disabledOrHydrating.value && props.isLink,
            '!p-0': props.isInsideText,
        },
    ];
});

const getIconClasses = (
    iconOnly: boolean,
    isLink: boolean,
    isDisabled: boolean,
    isLeft: boolean
) => {
    const hoverClass =
        isDisabled && isLink ? computedColor.value.fillHover : '';
    const marginClass = iconOnly ? '' : isLeft ? 'mr-1.5' : 'ml-1.5';
    const fill = isLink
        ? computedColor.value.fillLink
        : computedColor.value.fill;

    return [hoverClass, fill, marginClass];
};

const computedIconClassLeft = computed(() => {
    return getIconClasses(
        props.iconOnly,
        props.isLink,
        !disabledOrHydrating.value,
        true
    );
});

const computedIconClassRight = computed(() => {
    return getIconClasses(
        props.iconOnly,
        props.isLink,
        !disabledOrHydrating.value,
        false
    );
});

const computedIconSize = computed(() => {
    const size = props.size;
    return iconSizes[size] || '14';
});

const contentHolderClasses = computed(() => {
    return [
        { 'opacity-0': props.isLoading },
        sizeRelatedContentHolderClasses[props.size],
    ];
});

const textHolderClasses = computed(() => {
    return sizeRelatedTextHolderClasses[props.size];
});
</script>

<style scoped>
.loading::after {
    @apply content-[''] absolute w-[16px] h-[16px] inset-0 m-auto border-2 border-transparent border-t-background-1 rounded-[50%] animate-spin;
}
</style>
